import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useFormValidation from '../../hooks/useUnifiedLoginFormValidation';
import Button, { FlexButtonGroup } from '@pattern-library/buttons';
import TextInput from '@pattern-library/text-input';
import ReCaptcha from '../ReCaptcha/ReCaptcha';

const propTypes = {
    formAction: PropTypes.string.isRequired,
    antiForgeryToken: PropTypes.string.isRequired,
    buttonProps: PropTypes.objectOf(PropTypes.string).isRequired,
    emailInputProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
        .isRequired,
    captchaCallbackName: PropTypes.string.isRequired,
    captchaErrorId: PropTypes.string.isRequired,
    captchaSiteKey: PropTypes.string.isRequired,
    hasInitialCaptchaError: PropTypes.bool.isRequired,
    captchaDisabled: PropTypes.bool,
    disableCaptchaKey: PropTypes.string
};

function ForgotUsernameForm(props) {
    const {
        formAction,
        antiForgeryToken,
        buttonProps,
        emailInputProps,
        captchaCallbackName,
        captchaErrorId,
        captchaSiteKey,
        hasInitialCaptchaError,
        captchaDisabled,
        disableCaptchaKey
    } = props;
    const [hasCaptchaError, setHasCaptchaError] = useState(hasInitialCaptchaError);
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

    const schema = {
        [emailInputProps.name]: [
            {
                validationMethod: 'required',
                errorMessage: 'Enter an email address'
            },
            {
                validationMethod: 'email',
                errorMessage: 'Please enter a valid email address'
            }
        ]
    };
    const { onSubmit, hasError, errorMessage } = useFormValidation({ schema });

    const submitIfCaptchaVerified = useCallback(
        e => {
            if (isCaptchaVerified || captchaDisabled) {
                e.target.submit();
            } else {
                setHasCaptchaError(true);
            }
        },
        [isCaptchaVerified, captchaDisabled]
    );

    const handleSubmit = useCallback(
        e => {
            onSubmit(e, submitIfCaptchaVerified);
        },
        [onSubmit, submitIfCaptchaVerified]
    );

    const onVerifyCaptcha = useCallback(() => {
        setIsCaptchaVerified(true);
        setHasCaptchaError(false);
    }, []);

    const onExpiredCaptcha = useCallback(() => {
        setIsCaptchaVerified(false);
    }, []);

    useEffect(() => {
        const captchaErrorEl = document.getElementById(captchaErrorId);
        if (captchaErrorEl) {
            captchaErrorEl.classList.toggle('asc-hidden', !hasCaptchaError);
        }
    }, [captchaErrorId, hasCaptchaError]);

    const buttonDescribedBy =
        [buttonProps.customAttrs?.['aria-describedby'], hasCaptchaError ? captchaErrorId : null]
            .filter(Boolean)
            .join(' ') || undefined;

    return (
        <form
            action={formAction}
            method="post"
            onSubmit={handleSubmit}
            autoComplete="off"
            noValidate
        >
            <div dangerouslySetInnerHTML={{ __html: antiForgeryToken }} />
            {disableCaptchaKey && (
                <input
                    type="hidden"
                    id="DisableCaptchaKey"
                    name="DisableCaptchaKey"
                    value={disableCaptchaKey}
                />
            )}
            <TextInput
                {...emailInputProps}
                isRequired
                type="email"
                hasError={hasError(emailInputProps.name)}
                errorMessage={errorMessage(emailInputProps.name)}
            />

            <ReCaptcha
                id="forgot-username-captcha"
                callbackName={captchaCallbackName}
                verifyCallback={onVerifyCaptcha}
                expiredCallback={onExpiredCaptcha}
                siteKey={captchaSiteKey}
            />

            <FlexButtonGroup alignment="center" className="asc-mt-4 asc-mb-0">
                <Button
                    type="submit"
                    className="asc-mtb-0"
                    {...buttonProps}
                    customAttrs={{
                        ...buttonProps.customAttrs,
                        'aria-describedby': buttonDescribedBy
                    }}
                />
            </FlexButtonGroup>
        </form>
    );
}

ForgotUsernameForm.propTypes = propTypes;
export default ForgotUsernameForm;
